import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signInWithEmail, signUpWithEmail, signInWithGoogle, signOut, sendPasswordReset, resendVerificationEmail, auth } from '@/lib/firebase';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Mail, Lock, Loader2, AlertCircle, User, Phone, CheckCircle2, ArrowLeft } from 'lucide-react';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useTheme } from '@/contexts/ThemeContext';
import logoImg from '@/logo.png';
import logoWhiteImg from '@/logo-white.png';

type View = 'login' | 'signup' | 'verify-email' | 'forgot-password' | 'reset-sent';

function formatWhatsApp(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();

  const needsVerify = searchParams.get('verify') === 'true';

  const [view, setView] = useState<View>(needsVerify ? 'verify-email' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const currentLogo = theme === 'dark' ? logoWhiteImg : logoImg;

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signUpWithEmail(email, password, name);

      // Save whatsapp to backend
      if (whatsapp) {
        try {
          await api.patch('/api/users/me', { name, whatsapp: whatsapp.replace(/\D/g, '') });
        } catch {
          // User may not exist in DB yet, will be saved on first /api/users/me GET
        }
      }

      setView('verify-email');
    } catch (err: any) {
      console.error('Signup error:', err);
      setError(
        err.code === 'auth/email-already-in-use'
          ? t('auth.errors.emailInUse', 'Email already in use')
          : err.code === 'auth/weak-password'
          ? t('auth.errors.weakPassword', 'Password too weak (minimum 6 characters)')
          : t('auth.errors.loginFailed', 'Sign up failed. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const credential = await signInWithEmail(email, password);

      if (!credential.user.emailVerified) {
        setView('verify-email');
        setLoading(false);
        return;
      }

      navigate('/');
    } catch (err: any) {
      console.error('Auth error:', err);
      setError(
        err.code === 'auth/user-not-found'
          ? t('auth.errors.userNotFound', 'User not found')
          : err.code === 'auth/wrong-password'
          ? t('auth.errors.wrongPassword', 'Incorrect password')
          : err.code === 'auth/invalid-credential'
          ? t('auth.errors.wrongPassword', 'Incorrect email or password')
          : t('auth.errors.loginFailed', 'Login failed. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setError('');
    setLoading(true);

    try {
      await signInWithGoogle();
      navigate('/');
    } catch (err: any) {
      console.error('Google auth error:', err);
      setError(t('auth.errors.googleFailed', 'Google login failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await resendVerificationEmail();
      setSuccess(t('auth.verificationResent', 'Verification email resent!'));
      setResendCooldown(60);
    } catch (err: any) {
      console.error('Resend error:', err);
      setError(t('auth.errors.resendFailed', 'Failed to resend email. Try again later.'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await sendPasswordReset(email);
      setView('reset-sent');
    } catch (err: any) {
      console.error('Reset error:', err);
      setError(
        err.code === 'auth/user-not-found'
          ? t('auth.errors.userNotFound', 'User not found')
          : t('auth.errors.resetFailed', 'Failed to send reset email. Try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setView('login');
    setError('');
    setSuccess('');
    // Sign out if user is logged in but not verified
    if (auth.currentUser && !auth.currentUser.emailVerified) {
      signOut();
    }
  };

  const handleCheckVerification = async () => {
    setLoading(true);
    setError('');
    try {
      const user = auth.currentUser;
      if (user) {
        await user.reload();
        if (user.emailVerified) {
          // Force token refresh so AuthContext picks up the new state
          await user.getIdToken(true);
          window.location.href = '/';
          return;
        }
      }
      setError(t('auth.errors.notVerifiedYet', 'Email not verified yet. Check your inbox and spam folder.'));
    } catch {
      setError(t('auth.errors.loginFailed', 'Error checking verification. Try again.'));
    } finally {
      setLoading(false);
    }
  };

  const renderVerifyEmail = () => (
    <>
      <CardHeader className="space-y-1 pb-6 text-center">
        <div className="flex justify-center mb-2">
          <Mail className="h-12 w-12 text-blue-500" />
        </div>
        <CardTitle className="text-2xl font-bold">
          {t('auth.verifyEmail', 'Verify your email')}
        </CardTitle>
        <CardDescription>
          {t('auth.verifyEmailDescription', 'We sent a verification link to your email. Click the link to activate your account.')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="bg-red-50 dark:bg-red-950/30 border-2 border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-600 dark:text-red-400 font-medium">{error}</p>
          </div>
        )}
        {success && (
          <div className="bg-green-50 dark:bg-green-950/30 border-2 border-green-200 dark:border-green-800 rounded-lg p-3 flex items-start gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-600 dark:text-green-400 font-medium">{success}</p>
          </div>
        )}

        <Button
          onClick={handleCheckVerification}
          className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold"
          disabled={loading}
        >
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('auth.alreadyVerified', 'I already verified')}
        </Button>

        <Button
          variant="outline"
          onClick={handleResendVerification}
          className="w-full h-12"
          disabled={loading || resendCooldown > 0}
        >
          {resendCooldown > 0
            ? t('auth.resendIn', 'Resend in {{seconds}}s', { seconds: resendCooldown })
            : t('auth.resendEmail', 'Resend verification email')}
        </Button>

        <button
          type="button"
          onClick={handleBackToLogin}
          className="flex items-center justify-center gap-1 w-full text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('auth.backToLogin', 'Back to login')}
        </button>
      </CardContent>
    </>
  );

  const renderForgotPassword = () => (
    <>
      <CardHeader className="space-y-1 pb-6">
        <CardTitle className="text-2xl font-bold text-center">
          {t('auth.forgotPassword', 'Forgot password')}
        </CardTitle>
        <CardDescription className="text-center">
          {t('auth.forgotPasswordDescription', 'Enter your email and we\'ll send you a reset link')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="bg-red-50 dark:bg-red-950/30 border-2 border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-600 dark:text-red-400 font-medium">{error}</p>
          </div>
        )}

        <form onSubmit={handleForgotPassword} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reset-email">{t('auth.email', 'Email')}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                id="reset-email"
                type="email"
                placeholder={t('auth.emailPlaceholder', 'your@email.com')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="pl-10 h-12"
                disabled={loading}
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold"
            disabled={loading}
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('auth.sendResetLink', 'Send reset link')}
          </Button>
        </form>

        <button
          type="button"
          onClick={handleBackToLogin}
          className="flex items-center justify-center gap-1 w-full text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('auth.backToLogin', 'Back to login')}
        </button>
      </CardContent>
    </>
  );

  const renderResetSent = () => (
    <>
      <CardHeader className="space-y-1 pb-6 text-center">
        <div className="flex justify-center mb-2">
          <CheckCircle2 className="h-12 w-12 text-green-500" />
        </div>
        <CardTitle className="text-2xl font-bold">
          {t('auth.resetSent', 'Email sent!')}
        </CardTitle>
        <CardDescription>
          {t('auth.resetSentDescription', 'Check your inbox for the password reset link.')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button
          onClick={handleBackToLogin}
          className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold"
        >
          {t('auth.backToLogin', 'Back to login')}
        </Button>
      </CardContent>
    </>
  );

  const renderLoginSignup = () => {
    const isSignUp = view === 'signup';

    return (
      <>
        <CardHeader className="space-y-1 pb-6">
          <CardTitle className="text-2xl font-bold text-center">
            {isSignUp ? t('auth.createAccount', 'Create Account') : t('auth.welcomeBack', 'Welcome back')}
          </CardTitle>
          <CardDescription className="text-center">
            {isSignUp
              ? t('auth.createAccountDescription', 'Create your account to start generating amazing images')
              : t('auth.loginDescription', 'Sign in to continue generating professional images')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Google Sign In */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 border-2 hover:bg-gray-50 dark:hover:bg-gray-700"
            onClick={handleGoogleAuth}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <svg className="h-5 w-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {t('auth.signInWithGoogle', 'Continue with Google')}
              </>
            )}
          </Button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 font-medium">{t('common.or', 'or')}</span>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-950/30 border-2 border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2 animate-shake">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600 dark:text-red-400 font-medium">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={isSignUp ? handleSignUp : handleLogin} className="space-y-4">
            {/* Name - sign up only */}
            {isSignUp && (
              <div className="space-y-2">
                <Label htmlFor="name">{t('auth.name', 'Name')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder={t('auth.namePlaceholder', 'Your name')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="pl-10 h-12"
                    disabled={loading}
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email', 'Email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder', 'your@email.com')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-10 h-12"
                  disabled={loading}
                />
              </div>
            </div>

            {/* WhatsApp - sign up only */}
            {isSignUp && (
              <div className="space-y-2">
                <Label htmlFor="whatsapp">{t('auth.whatsapp', 'WhatsApp')}</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="whatsapp"
                    type="tel"
                    placeholder="(11) 99999-9999"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(formatWhatsApp(e.target.value))}
                    className="pl-10 h-12"
                    disabled={loading}
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password', 'Password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="pl-10 h-12"
                  disabled={loading}
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : isSignUp ? (
                t('auth.createAccount', 'Create Account')
              ) : (
                t('auth.login', 'Login')
              )}
            </Button>
          </form>

          {/* Forgot password - login only */}
          {!isSignUp && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => { setView('forgot-password'); setError(''); }}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                disabled={loading}
              >
                {t('auth.forgotPassword', 'Forgot password?')}
              </button>
            </div>
          )}

          {/* Toggle Sign Up / Sign In */}
          <div className="text-center pt-2">
            <button
              type="button"
              onClick={() => {
                setView(isSignUp ? 'login' : 'signup');
                setError('');
              }}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors"
              disabled={loading}
            >
              {isSignUp ? (
                <>
                  {t('auth.alreadyHaveAccount', 'Already have an account?')} <span className="text-blue-600 font-semibold">{t('auth.login', 'Login')}</span>
                </>
              ) : (
                <>
                  {t('auth.dontHaveAccount', "Don't have an account?")} <span className="text-blue-600 font-semibold">{t('auth.createAccount', 'Create Account')}</span>
                </>
              )}
            </button>
          </div>
        </CardContent>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Top Actions */}
      <div className="absolute top-6 right-6 z-50 flex items-center gap-2">
        <ThemeToggle />
        <LanguageSwitcher />
      </div>

      {/* Main Content */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo & Title */}
          <div className="text-center mb-8 animate-fade-in">
            <div className="flex justify-center mb-4">
              <img src={currentLogo} alt="Cataloga" className="h-20 w-auto" />
            </div>
            <p className="text-gray-600 dark:text-gray-400">
              {t('auth.tagline', 'Transform your product photos with AI')}
            </p>
          </div>

          {/* Card */}
          <Card className="backdrop-blur-lg bg-white/90 dark:bg-gray-800/90 border-0 shadow-2xl animate-slide-up">
            {(view === 'login' || view === 'signup') && renderLoginSignup()}
            {view === 'verify-email' && renderVerifyEmail()}
            {view === 'forgot-password' && renderForgotPassword()}
            {view === 'reset-sent' && renderResetSent()}
          </Card>

          {/* Footer */}
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-8">
            © 2024 Cataloga. {t('common.allRightsReserved', 'All rights reserved.')}
          </p>
        </div>
      </div>
    </div>
  );
}
